import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import { getServicesManager } from 'services';
import { TeamInvitation } from 'interfaces/db';
import { Typography } from '../../interfaces/typography';
import { ExternalLink, UserPlus, X } from 'lucide-react';

export const OnboardingGuide: React.FC = () => {
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [showExtensionGuide, setShowExtensionGuide] = useState(false);
  const [showInvitationGuide, setShowInvitationGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.value);

  useEffect(() => {
    const checkChromeExtension = () => {
      return chrome !== undefined && chrome.runtime !== undefined && chrome.runtime.sendMessage !== undefined;
    };
    
    const loadData = async () => {
      const isChromeEnabled = checkChromeExtension();
      
      if (user?.can_dial && user?.team_is_active && !isChromeEnabled) {
        setShowExtensionGuide(true);
      }
      
      try {
        const invitations = await getServicesManager().getReceivedTeamInvitations();
        if (invitations && invitations.length > 0) {
          setPendingInvitations(invitations);
          setShowInvitationGuide(true);
        }
      } catch (error) {
        console.error('Failed to load invitations:', error);
      }
      
      setLoading(false);
    };
    
    if (user) {
      loadData();
    }
  }, [user?.user_id]);

  const handleGoToInvitations = () => {
    navigate('/invitations');
  };

  const handleDismissExtension = () => {
    setShowExtensionGuide(false);
  };

  const handleDismissInvitation = () => {
    setShowInvitationGuide(false);
  };

  if (loading || (!showExtensionGuide && !showInvitationGuide)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-md space-y-2">
      {showInvitationGuide && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 animate-slide-up">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <UserPlus size={14} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <Typography variant="smallParagraph" color="#1F2937" fontWeight={600}>
                  Team Invitations
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '2px' }}>
                  {pendingInvitations.length} pending invitation{pendingInvitations.length > 1 ? 's' : ''}
                </Typography>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={handleGoToInvitations}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
              >
                <UserPlus size={12} />
                View
              </button>
              <button
                onClick={handleDismissInvitation}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showExtensionGuide && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 animate-slide-up">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <ExternalLink size={14} className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <Typography variant="smallParagraph" color="#1F2937" fontWeight={600}>
                  Chrome Extension
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '2px' }}>
                  Install extension for dialing
                </Typography>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <a
                href="https://app.trellus.ai/install"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors"
              >
                <ExternalLink size={12} />
                Install
              </a>
              <button
                onClick={handleDismissExtension}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};